import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

export class VideoExporter {
  private muxer: Muxer<ArrayBufferTarget> | null = null;
  private videoEncoder: VideoEncoder | null = null;
  private audioEncoder: AudioEncoder | null = null;
  private width: number;
  private height: number;
  private fps: number;
  private keyframeInterval: number;

  constructor(width: number, height: number, fps = 30) {
    this.width = width;
    this.height = height;
    this.fps = fps;
    this.keyframeInterval = fps * 2;
  }

  async init(audioBuffer?: AudioBuffer | null) {
    this.muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: {
        codec: 'avc',
        width: this.width,
        height: this.height,
      },
      audio: audioBuffer ? {
        codec: 'aac',
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels,
      } : undefined,
      fastStart: 'in-memory',
    });

    this.videoEncoder = new VideoEncoder({
      output: (chunk, meta) => this.muxer?.addVideoChunk(chunk, meta),
      error: (e) => console.error('VideoEncoder error:', e),
    });

    this.videoEncoder.configure({
      codec: 'avc1.640033',
      width: this.width,
      height: this.height,
      displayWidth: this.width,
      displayHeight: this.height,
      bitrate: 5_000_000,
      framerate: this.fps,
      latencyMode: 'quality',
    });

    if (audioBuffer) {
      this.audioEncoder = new AudioEncoder({
        output: (chunk, meta) => {
          console.log(`Audio chunk produced: ${chunk.byteLength} bytes, ts: ${chunk.timestamp}`);
          this.muxer?.addAudioChunk(chunk, meta);
        },
        error: (e) => console.error('AudioEncoder error:', e),
      });

      this.audioEncoder.configure({
        codec: 'mp4a.40.2',
        numberOfChannels: audioBuffer.numberOfChannels,
        sampleRate: audioBuffer.sampleRate,
        bitrate: 128_000,
      });

      await this.encodeAudio(audioBuffer);
    }
  }

  private async encodeAudio(buffer: AudioBuffer) {
    if (!this.audioEncoder) return;

    const sampleRate = buffer.sampleRate;
    const numberOfChannels = buffer.numberOfChannels;
    const framesPerChunk = 2048;
    
    console.log(`Encoding audio: ${buffer.duration}s, ${buffer.length} samples, ${numberOfChannels} channels`);

    for (let i = 0; i < buffer.length; i += framesPerChunk) {
      // Backpressure to prevent overwhelming the audio encoder queue (prevents jitter and dropped audio chunks)
      while (this.audioEncoder.encodeQueueSize > 10) {
        await new Promise(r => setTimeout(r, 1));
      }

      const currentFrames = Math.min(framesPerChunk, buffer.length - i);
      const data = new Float32Array(currentFrames * numberOfChannels);
      
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = buffer.getChannelData(channel);
        // Lay out channels sequentially for planar format (no manual interleaving required)
        data.set(channelData.subarray(i, i + currentFrames), channel * currentFrames);
      }

      const audioData = new AudioData({
        format: 'f32-planar',
        sampleRate,
        numberOfFrames: currentFrames,
        numberOfChannels,
        timestamp: Math.round((i / sampleRate) * 1_000_000),
        data,
      });

      this.audioEncoder.encode(audioData);
      audioData.close();
    }
    
    await this.audioEncoder.flush();
    console.log('Audio encoding complete');
  }

  async encodeFrame(source: OffscreenCanvas, frameIndex: number) {
    if (!this.videoEncoder) return;

    while (this.videoEncoder.encodeQueueSize > 5) {
      await new Promise(r => setTimeout(r, 1));
    }

    const timestampUs = Math.round((frameIndex / this.fps) * 1_000_000);
    const keyFrame = frameIndex % this.keyframeInterval === 0;

    const frame = new VideoFrame(source, { timestamp: timestampUs });
    this.videoEncoder.encode(frame, { keyFrame });
    frame.close();
  }

  async finalize(): Promise<Blob> {
    if (!this.videoEncoder || !this.muxer) return new Blob();

    await this.videoEncoder.flush();
    this.videoEncoder.close();
    
    if (this.audioEncoder) {
      await this.audioEncoder.flush();
      this.audioEncoder.close();
    }

    this.muxer.finalize();
    const buffer: ArrayBuffer = this.muxer.target.buffer;
    return new Blob([buffer], { type: 'video/mp4' });
  }
}
